import { Pencil, Trash2 } from 'lucide-react';
import { type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { LiksButton } from '../../../components/liks/LiksButton/LiksButton';
import { LiksConfirmDialog } from '../../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import { LiksDialog } from '../../../components/liks/LiksDialog/LiksDialog';
import { LiksIconPicker } from '../../../components/liks/LiksIconPicker/LiksIconPicker';
import { useI18n } from '../../../i18n/useI18n';
import {
  useDeleteKnowledgeDatabaseMutation,
  useUpdateKnowledgeDatabaseMutation,
} from '../../../features/knowledge/mutations/useKnowledgeDatabaseMutations';
import { useKnowledgeDatabaseSharedStore } from '../../../features/knowledge/stores/useKnowledgeDatabaseSharedStore';
import { useKnowledgeSettingsTabStore } from '../../../features/knowledge/stores/useKnowledgeSettingsTabStore';
import { toastMutationError } from '../../../query/mutationToasts';
import { toast } from '../../../toast/toast';
import type { KnowledgeBase } from '../../../types/knowledge';
import {
  DEFAULT_LUCIDE_ICON_NAME,
  normalizeLucideIconName,
} from '../../../utils/lucideIconCatalog';
import '../CreateKnowledgeDatabaseDialog.css';
import '../knowledgeDatabaseShared.css';
import './KnowledgeSettingsTab.css';

type KnowledgeSettingsTabProps = {
  databaseId: string;
  database: KnowledgeBase;
  canDeleteDatabase: boolean;
};

export function KnowledgeSettingsTab({
  databaseId,
  database,
  canDeleteDatabase,
}: KnowledgeSettingsTabProps) {
  const { translate } = useI18n();
  const navigate = useNavigate();
  const updateDatabaseMutation = useUpdateKnowledgeDatabaseMutation();
  const deleteDatabaseMutation = useDeleteKnowledgeDatabaseMutation();

  const isEditModalOpen = useKnowledgeSettingsTabStore(
    (state) => state.isEditModalOpen,
  );
  const editName = useKnowledgeSettingsTabStore((state) => state.editName);
  const editDescription = useKnowledgeSettingsTabStore(
    (state) => state.editDescription,
  );
  const editIcon = useKnowledgeSettingsTabStore((state) => state.editIcon);
  const openEditModal = useKnowledgeSettingsTabStore(
    (state) => state.openEditModal,
  );
  const closeEditModal = useKnowledgeSettingsTabStore(
    (state) => state.closeEditModal,
  );
  const setEditName = useKnowledgeSettingsTabStore(
    (state) => state.setEditName,
  );
  const setEditDescription = useKnowledgeSettingsTabStore(
    (state) => state.setEditDescription,
  );
  const setEditIcon = useKnowledgeSettingsTabStore(
    (state) => state.setEditIcon,
  );
  const pendingDelete = useKnowledgeDatabaseSharedStore(
    (state) => state.pendingDelete,
  );
  const setPendingDelete = useKnowledgeDatabaseSharedStore(
    (state) => state.setPendingDelete,
  );

  const beginEdit = () => {
    openEditModal({
      name: database.name ?? '',
      description: database.description ?? '',
      icon: normalizeLucideIconName(database.icon ?? DEFAULT_LUCIDE_ICON_NAME),
    });
  };

  const handleSaveDatabase = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    updateDatabaseMutation.mutate(
      {
        database,
        updates: {
          name: editName,
          description: editDescription,
          icon: editIcon,
        },
      },
      {
        onSuccess: () => {
          toast.success({
            title: translate('knowledge.updateSuccessTitle'),
            description: '',
          });
          closeEditModal();
        },
        onError: (error) => {
          void toastMutationError(
            error,
            translate('knowledge.updateErrorTitle'),
          );
        },
      },
    );
  };

  const confirmDeleteDatabase = () => {
    if (
      !pendingDelete ||
      pendingDelete.type !== 'database' ||
      deleteDatabaseMutation.isPending
    ) {
      return;
    }

    deleteDatabaseMutation.mutate(databaseId, {
      onSuccess: () => {
        toast.success({
          title: translate('knowledge.deleteSuccessTitle'),
          description: translate('knowledge.deleteDatabaseSuccessDescription', {
            name: database.name ?? '',
          }),
        });
        navigate('/knowledge');
      },
      onError: (error) => {
        void toastMutationError(
          error,
          translate('knowledge.deleteErrorTitle'),
          translate('knowledge.deleteErrorDescription'),
        );
      },
      onSettled: () => {
        setPendingDelete(null);
      },
    });
  };

  return (
    <>
      <section className="knowledge-database__panel knowledge-database__settings">
        <div className="knowledge-database__settings-section">
          <h2 className="knowledge-database__settings-title">
            {translate('knowledge.form.editTitle')}
          </h2>
          <p className="knowledge-database__settings-description">
            {translate('knowledge.settings.editDescription')}
          </p>
          <LiksButton onClick={beginEdit}>
            <Pencil aria-hidden /> {translate('knowledge.form.editTitle')}
          </LiksButton>
        </div>
        {canDeleteDatabase && (
          <div className="knowledge-database__settings-section">
            <h2 className="knowledge-database__settings-title">
              {translate('knowledge.settings.deleteTitle')}
            </h2>
            <p className="knowledge-database__settings-description">
              {translate('knowledge.settings.deleteDescription', {
                name: database.name ?? '',
              })}
            </p>
            <LiksButton
              variant="danger"
              onClick={() =>
                setPendingDelete({ type: 'database', id: databaseId })
              }
            >
              <Trash2 aria-hidden />{' '}
              {translate('knowledge.settings.deleteTitle')}
            </LiksButton>
          </div>
        )}
      </section>

      <LiksDialog
        isOpen={isEditModalOpen}
        as="form"
        size="md"
        title={translate('knowledge.form.editTitle')}
        onClose={closeEditModal}
        onSubmit={handleSaveDatabase}
        closeLabel={translate('knowledge.form.close')}
        footer={
          <>
            <LiksButton
              type="button"
              variant="secondary"
              onClick={closeEditModal}
            >
              {translate('knowledge.form.cancel')}
            </LiksButton>
            <LiksButton
              type="submit"
              variant="primary"
              disabled={editName.trim() === '' || editIcon.trim() === ''}
            >
              {translate('knowledge.form.save')}
            </LiksButton>
          </>
        }
      >
        <label className="liks-knowledge-create__field">
          <span className="liks-knowledge-create__label">
            {translate('knowledge.form.titleLabel')}
          </span>
          <input
            className="liks-knowledge-create__input"
            value={editName}
            onChange={(event) => setEditName(event.target.value)}
            placeholder={translate('knowledge.form.titlePlaceholder')}
            required
            autoFocus
          />
        </label>

        <label className="liks-knowledge-create__field">
          <span className="liks-knowledge-create__label">
            {translate('knowledge.form.descriptionLabel')}
          </span>
          <textarea
            className="liks-knowledge-create__textarea"
            value={editDescription}
            onChange={(event) => setEditDescription(event.target.value)}
            placeholder={translate('knowledge.form.descriptionPlaceholder')}
            rows={4}
          />
        </label>

        <LiksIconPicker
          value={editIcon}
          onChange={setEditIcon}
          label={translate('knowledge.form.iconLabel')}
          searchPlaceholder={translate(
            'knowledge.iconPicker.searchPlaceholder',
          )}
          searchClearLabel={translate('common.search.clear')}
          noResultsLabel={translate('knowledge.iconPicker.noResults')}
          getOptionLabel={(iconName) =>
            translate('knowledge.iconPicker.optionLabel', {
              icon: iconName,
            })
          }
        />
      </LiksDialog>

      <LiksConfirmDialog
        isOpen={pendingDelete?.type === 'database'}
        title={translate('knowledge.deleteDatabaseTitle')}
        message={translate('knowledge.deleteDatabaseMessage', {
          name: database.name ?? '',
        })}
        confirmLabel={
          deleteDatabaseMutation.isPending
            ? translate('common.deleting')
            : translate('knowledge.deleteConfirm')
        }
        cancelLabel={translate('knowledge.deleteCancel')}
        isBusy={deleteDatabaseMutation.isPending}
        onConfirm={() => void confirmDeleteDatabase()}
        onCancel={() => {
          if (!deleteDatabaseMutation.isPending) {
            setPendingDelete(null);
          }
        }}
      />
    </>
  );
}
